import { FileText, User, Settings, Eye, Play, CheckCircle2, XCircle, RefreshCw, Search, AlertTriangle, Package, HelpCircle, Link } from 'lucide-react';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { cn, formatDateTime } from '@/lib/utils';
import type { AuditLogEntry, AuditEventType } from '@/types';

interface AuditLogTableProps {
  entries: AuditLogEntry[];
  onViewDetails: (entry: AuditLogEntry) => void;
  className?: string | undefined;
}

const eventTypeConfig: Record<AuditEventType, {
  label: string;
  variant: 'default' | 'secondary' | 'outline' | 'destructive';
  icon: typeof FileText;
}> = {
  MigrationStarted: { label: 'Migration Started', variant: 'default', icon: Play },
  MigrationCompleted: { label: 'Migration Complete', variant: 'default', icon: CheckCircle2 },
  MigrationFailed: { label: 'Migration Failed', variant: 'destructive', icon: XCircle },
  MigrationCancelled: { label: 'Migration Cancelled', variant: 'outline', icon: XCircle },
  MigrationRetried: { label: 'Migration Retried', variant: 'secondary', icon: RefreshCw },
  ModuleStarted: { label: 'Module Started', variant: 'default', icon: Package },
  ModuleCompleted: { label: 'Module Complete', variant: 'default', icon: CheckCircle2 },
  ModuleFailed: { label: 'Module Failed', variant: 'destructive', icon: XCircle },
  ModuleRetried: { label: 'Module Retried', variant: 'secondary', icon: RefreshCw },
  ConfigurationChanged: { label: 'Config Changed', variant: 'secondary', icon: Settings },
  UserLoggedIn: { label: 'Login', variant: 'default', icon: User },
  UserLoggedOut: { label: 'Logout', variant: 'outline', icon: User },
  BridgeLookupPerformed: { label: 'Bridge Lookup', variant: 'secondary', icon: Search },
  BridgeDuplicateDetected: { label: 'Bridge Duplicate', variant: 'destructive', icon: AlertTriangle },
  MigrationOrganizationAttached: { label: 'Organization Attached', variant: 'secondary', icon: Link },
};

const fallbackConfig = {
  label: 'Unknown',
  variant: 'outline' as const,
  icon: HelpCircle,
};

export function AuditLogTable({
  entries,
  onViewDetails,
  className,
}: AuditLogTableProps) {
  return (
    <div className={cn('rounded-md border', className)}>
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Timestamp</TableHead>
            <TableHead>Event</TableHead>
            <TableHead>User</TableHead>
            <TableHead>Description</TableHead>
            <TableHead className="w-[70px]"></TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {entries.length === 0 ? (
            <TableRow>
              <TableCell colSpan={5} className="h-24 text-center text-muted-foreground">
                No audit log entries found.
              </TableCell>
            </TableRow>
          ) : (
            entries.map((entry) => {
              const config = eventTypeConfig[entry.eventType] ?? fallbackConfig;
              const Icon = config.icon;

              return (
                <TableRow key={entry.id}>
                  <TableCell className="text-sm text-muted-foreground whitespace-nowrap">
                    {formatDateTime(entry.timestamp)}
                  </TableCell>
                  <TableCell>
                    <Badge variant={config.variant} className="gap-1">
                      <Icon className="h-3 w-3" />
                      {config.label}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    <p className="font-medium text-sm">{entry.userName}</p>
                  </TableCell>
                  <TableCell>
                    <p className="text-sm max-w-md truncate">{entry.description}</p>
                    {entry.moduleKey && (
                      <p className="text-xs text-muted-foreground">{entry.moduleKey}</p>
                    )}
                  </TableCell>
                  <TableCell>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-8 w-8 p-0"
                      onClick={() => onViewDetails(entry)}
                    >
                      <Eye className="h-4 w-4" />
                      <span className="sr-only">View details</span>
                    </Button>
                  </TableCell>
                </TableRow>
              );
            })
          )}
        </TableBody>
      </Table>
    </div>
  );
}
